/**
 * [29/10/2026] O que sai para terceiros (GA4 hoje) a partir de um evento do
 * analytics interno. Regra: LISTA PERMITIDA por evento. Evento fora da lista
 * sai só com o nome; evento em EVENTOS_SO_INTERNOS não sai de jeito nenhum.
 *
 * Motivo: o GA4 recebia a metadata integral, inclusive tag de avaliação
 * infantil (`tag_added`), humor (`emocao_id`) e energia/sobrecarga.
 * Ampliar a lista exige conferir que o parâmetro não revela assunto,
 * sentimento, origem (ângulo) nem recurso sensível.
 */

/** Nunca saem para terceiros. Ficam, no máximo, no analytics interno. */
export const EVENTOS_SO_INTERNOS: ReadonlySet<string> = new Set([
  "atypical_markers_saved",
  "trilha_atipica_step_opened",
  "assistant_crisis_shortcircuit",
  "assistant_atypical_context",
  "assistant_culpa_context",
  "assistant_triage_intent",
  "brincadeira_do_dia_feita",
  "brincadeira_do_dia_sem_catalogo",
  "push_prompt_daily_play_shown",
  "push_prompt_daily_play_accepted",
  "push_prompt_daily_play_dismissed",
  "push_prompt_daily_play_failed",
]);

/** Nunca saem para terceiros, em nenhum evento — mesmo se alguém os listar abaixo. */
export const PARAMS_PROIBIDOS: ReadonlySet<string> = new Set([
  "tag_added", "has_tags", "energia", "rede_apoio", "peso_atual", "emocao_id",
  "scores", "tipo", "tema", "intent", "context", "category", "material_id",
  "source_angle", "angle",
  // [02/11/2026] plano no momento do evento: só tabela interna.
  "plan", "in_trial",
]);

export const PARAMS_PERMITIDOS_GA4: Readonly<Record<string, readonly string[]>> = {
  signup_completed:          ["method"],
  onboarding_step_started:   ["question", "step_index", "total_steps", "resumed"],
  onboarding_step_completed: ["question", "step_index", "total_steps"],
  onboarding_completed:      [],
  onboarding_offer_shown:    ["platform", "intro_offer", "appearance"],
  onboarding_offer_cta_clicked: ["platform", "intro_offer", "appearance"],
  onboarding_offer_dismissed: ["platform", "intro_offer", "appearance"],
  wow_flow_started:          [],
  wow_flow_completed_v2:     ["steps", "duration_ms"],
  first_perfil_view:         [],
  first_checkin:             [],
  planos_opened:             ["required_plan"],
  planos_view:               [],
  planos_abandoned:          ["interacted", "dwell_ms"],
  upsell_impression:         ["required_plan", "passive"],
  paywall_shown:             ["required_plan", "passive"],
  paywall_cta_clicked:       ["required_plan", "interval", "direct_checkout"],
  paywall_dismissed:         ["via"],
  checkout_started:          ["plan_tier", "interval", "current_plan", "channel"],
  checkout_failed:           ["plan_tier", "interval", "channel"],
  purchase:                  ["plan_tier", "interval", "current_plan", "channel"],
  purchase_failed:           ["plan_tier", "interval", "channel"],
  purchase_cancelled:        ["plan_tier", "interval", "channel"],
};

type Primitivo = string | number | boolean;

/** `null` = o evento não sai. Objeto = só os parâmetros permitidos (primitivos). */
export function paramsParaTerceiros(
  evento: string,
  metadata: Record<string, unknown> = {},
): Record<string, Primitivo> | null {
  if (EVENTOS_SO_INTERNOS.has(evento)) return null;
  const ok = PARAMS_PERMITIDOS_GA4[evento] ?? [];
  const saida: Record<string, Primitivo> = {};
  for (const k of ok) {
    if (PARAMS_PROIBIDOS.has(k)) continue;
    const v = metadata?.[k];
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") saida[k] = v;
  }
  return saida;
}
